import React, { useEffect, useState, useMemo } from 'react';
import {
  getDMSAccountDetailsFromEIS,
  updateSelectedDMSAccounts,
  getDMSInfoBasedOnApis,
  updateDMSAccountsForAllLocations,
  showAllDMSAccountsLocations,
  updateDMSAccountsOnSelection
} from '../../redux/connectedComponent/selectedDMS/selectedDMS.slice';
import {
  clearCppSelections,
  setCppAvailableLocations
} from '../../redux/connectedComponent/privacyProtection/privacyProtection.slice';
import { setSubscriptionSettingsAvailableLocations } from '../../redux/connectedComponent/dealerApiPreferences/dealerApiPreferences.slice';
import { setProvisioning } from '../../redux/connectedComponent/provisionSettings/provisionSettings.slice';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '../../hooks/debounce';
import Stepper from './Stepper';
import { hideStep } from '../../utils/hideStep';
import { addNavListener } from '../../utils/popstate';
import { environmentURLs } from '../common/CaptureEnvironment';

const {
  DMS_ACCOUNT_TYPES_MAPPING
} = require('./stepperBody/stepperBodyComponents/DMSSelectionComponents/utils/constant');

function StepperContainer(props) {
    const dispatch = useDispatch();
    const state = useSelector(state => state);
    const [hideConfig, setHideConfig] = useState(true);

    const api_details = state?.solutionInfoSlice?.solutionInfo?.registration_details?.api_details;
    const appId = state?.solutionInfoSlice?.solutionInfo?.id;
    const token = state.loginDetailsSlice?.authenticationToken;
    const DMSConfigInfo = state.selectDMSSlice?.DMSConfigInfo;
    const redirectURL =
      state?.solutionInfoSlice?.solutionInfo?.provisioning_interface?.redirectURL;
    const entityID =
      state?.solutionInfoSlice?.solutionInfo?.provisioning_interface?.entityID;

    const { selectedLocationsWithCMFNumber, locationsToAddForSubManagementWithCMF } = state?.selectLocationsSlice;
    const { authenticationToken, allLocations } = state.loginDetailsSlice;
    const {
      getEISDMSInfo, isEISDMSInfoSuccess, updatedSelectedLocations,
      updatedSelectedAllLocations, showAllLocations
    } = state.selectDMSSlice;
    const { isApiProvidersPending, asyncAPIs, apiAndProviders, isAsyncAPIsPending, errorFetchingApiProviders, errorFetchingAsyncApis } = state.solutionInfoSlice;

    // derive dmsApiDetails from apiAndProviders and asyncAPIs
    const dmsApiDetails = useMemo(() => {
      const details = {};
      
      if (apiAndProviders && Array.isArray(apiAndProviders)) {
        for (const api of apiAndProviders) {
          const provider = api.apiProvider?.[0];
          if (provider?.dmsTypes && provider.dmsTypes.length > 0) {
            details[provider.id] = provider.dmsTypes;
          }
        }
      }
      
      if (asyncAPIs && Array.isArray(asyncAPIs)) {
        for (const api of asyncAPIs) {
          if (api.dmsTypes && api.dmsTypes.length > 0) {
            details[api.id] = api.dmsTypes;
          }
        }
      }
      
      return details;
    }, [apiAndProviders, asyncAPIs]);

    // determine whether any of the api has DMS, FM, CPP or Subscription Setting
    // to hide/show respective steps in the stepper
    const apisSystemInfo = useMemo(() => {
      // now that we dont have any multi apis
      const providers = apiAndProviders?.map(api => api.apiProvider?.[0]) || [];
      const allApis = [...providers, ...(asyncAPIs || [])];

      return {
        hasDealerApiPreferences: allApis.some(api => api?.hasDealerApiPreferences),
        hasPrivacyProtectionAPIs: allApis.some(api => api?.privacyProtectionEnabled),
        hasVerticalScopingAPIs: allApis.some(api => api?.verticalScopingEnabled && api?.fieldLimitingEnabled),
        hasDMSAPIs: allApis.some(api => api?.dmsTypes?.length > 0)
      };
    }, [apiAndProviders, asyncAPIs]);

    // Derive hide flags from apisSystemInfo
    const hideDMS = !apisSystemInfo.hasDMSAPIs;
    const hideCPP = !apisSystemInfo.hasPrivacyProtectionAPIs;
    const hideEditAPIField = !(environmentURLs.REACT_APP_SHOW_VERTICAL_SCOPING_SCREEN && apisSystemInfo.hasVerticalScopingAPIs);
    const hideManageApiSubscriptionSettings = !apisSystemInfo.hasDealerApiPreferences;
    const bodyConfig = useMemo(
      () =>
        hideStep(
          hideEditAPIField,
          hideCPP,
          hideDMS,
          hideConfig,
          hideManageApiSubscriptionSettings
        ),
      [
        hideEditAPIField,
        hideCPP,
        hideDMS,
        hideConfig,
        hideManageApiSubscriptionSettings,
      ]
    );

    useEffect(()=>{
        if(api_details && api_details.length>0 )
        {
          let apis = api_details.map(api => ({id: api?.id, apiType: api?.apiType}));
          dispatch(getDMSInfoBasedOnApis({ apis, token: token, appId: appId }));
        }
      },[api_details])

      useEffect(()=>{
        if(redirectURL){
          setHideConfig(false);
          dispatch(setProvisioning({redirectURL, entityID }))
        } else {
          setHideConfig(true);
        }
      },[redirectURL]);

      useDebounce(100, () => {
       if (!isApiProvidersPending && !isAsyncAPIsPending && !errorFetchingApiProviders && !errorFetchingAsyncApis) {
          if (apisSystemInfo.hasDMSAPIs) {
            let locations = props.navigatedFromSubscriptionManagement
              ? locationsToAddForSubManagementWithCMF
              : selectedLocationsWithCMFNumber;

            locations.forEach(locationData => {          
              if ( !getEISDMSInfo.some(
                  (dms) =>
                    dms.location === locationData.location &&
                    dms.cmfNumber === locationData.cmfNumber &&
                    dms.entityId === locationData.entityId
                )
              ) {
                dispatch(
                  getDMSAccountDetailsFromEIS({
                    location: locationData.location,
                    cmfNumber: locationData.cmfNumber,
                    storeId: locationData.storeId,
                    entityId: locationData.entityId,
                    token: authenticationToken,
                    dmsApiDetails,
                  })
                );
              }
            });
          }
        }
      },[isApiProvidersPending, isAsyncAPIsPending, apisSystemInfo.hasDMSAPIs, selectedLocationsWithCMFNumber, dmsApiDetails]);

      useDebounce(100, () => {
        dispatch(setCppAvailableLocations(selectedLocationsWithCMFNumber));
      }, [selectedLocationsWithCMFNumber]);

      useDebounce(100, () => {
        dispatch(setSubscriptionSettingsAvailableLocations(selectedLocationsWithCMFNumber));
      }, [selectedLocationsWithCMFNumber]);

      useEffect(() => {
        // clear any cpp selections that might have been left behind
        // in the state from a previous edit cpp operation
        if (apisSystemInfo.hasPrivacyProtectionAPIs) {
          dispatch(clearCppSelections());
        }
      }, [apisSystemInfo.hasPrivacyProtectionAPIs]);

      useEffect(() => {
        const filterBySelectedLocations = (
          dmsEISInfo,
          selectedLocationsDetails
        ) => {
          let res = [];
          res = dmsEISInfo.filter(dmsEisEntry => {
            return selectedLocationsDetails.find(selectedLocation => {
              return (
                selectedLocation.entityId === dmsEisEntry.entityId &&
                selectedLocation.cmfNumber === dmsEisEntry.cmfNumber
              );
            });
          });
          return res;
        };

        let locations = props.navigatedFromSubscriptionManagement ? locationsToAddForSubManagementWithCMF : selectedLocationsWithCMFNumber;
        const updatedSelectedDMSAccounts = filterBySelectedLocations(
          getEISDMSInfo,
          locations
        );
        dispatch(updateSelectedDMSAccounts(updatedSelectedDMSAccounts));
      }, [getEISDMSInfo, isEISDMSInfoSuccess, selectedLocationsWithCMFNumber]);

      useEffect(() => {
        const updatedAllLocations = Object.entries(DMS_ACCOUNT_TYPES_MAPPING).reduce((updatedLocation, [acctTypeSuffix, acctTypeName]) => {
          const updatedSelectedLocationsCopy = updatedSelectedLocations;

          const dmsAccountsArray = updatedSelectedLocations
            .map((item) => ({
              cmfNumber: item.cmfNumber,
              entityId: item.entityId,
              dmsAccount: item.dmsAccount,
              departments: item.departments,
              location: item.location,
            }))
            // Take only the first entry for each entityId (store)
            // each store has its own unique set of departments
            .filter(
              (v, i, a) =>
                a.findIndex(
                  (t) =>
                    t.entityId === v.entityId
                ) === i
            );


          let updatedDMSAccounts = [];
          updatedSelectedLocationsCopy.map(item => {
            let updatedDMSAccountWithType = { ...item, type: acctTypeName }
            if (
              updatedDMSAccountWithType.dmsAccountType === acctTypeSuffix &&
              !updatedDMSAccounts.some(ele => ele.entityId === updatedDMSAccountWithType.entityId)
            ) {
              updatedDMSAccounts.push(updatedDMSAccountWithType);
            }
          });

          updatedSelectedLocationsCopy.map(updatedLocation => {
            let updatedDMSAccountWithType = { ...updatedLocation, type: acctTypeName }
            if ( updatedDMSAccountWithType.dmsAccountType !== acctTypeSuffix ) {
              let apis = [];
              Object.entries(dmsApiDetails).forEach(([apiId]) => {
                if (dmsApiDetails[apiId] && Array.isArray(dmsApiDetails[apiId][0]) && dmsApiDetails[apiId][0].length > 0) {
                  // format: { (API-ID): [ ["Service", "Parts"] ] }
                  for (const dmsConfig of dmsApiDetails[apiId][0]) {
                    if (updatedDMSAccountWithType.type === dmsConfig) {
                      apis.push(apiId);
                    }
                  }
                } else if (
                  dmsApiDetails[apiId] && Array.isArray(dmsApiDetails[apiId]) && dmsApiDetails[apiId].length > 0) {
                    // format: { (API-ID): ["Service", "Parts"] }
                  for (const dmsConfig of dmsApiDetails[apiId]) {
                    if (updatedDMSAccountWithType.type === dmsConfig) {
                      apis.push(apiId);
                    }
                  }
                }
              });
              updatedDMSAccountWithType.api = apis;
              Object.entries(DMS_ACCOUNT_TYPES_MAPPING).forEach(([key, value]) => {
                if (updatedDMSAccountWithType.type === value)
                  updatedDMSAccountWithType.dmsAccountType = key;
              });

              updatedDMSAccountWithType.id = updatedDMSAccountWithType && updatedDMSAccountWithType.id && updatedDMSAccountWithType.id.substr(0,11) + Math.random().toString(36).substr(2, 9);
              const dmsArray = dmsAccountsArray.find(item => item.entityId === updatedDMSAccountWithType.entityId);

              updatedDMSAccountWithType = {
                ...updatedDMSAccountWithType,
                dmsAccount: dmsArray?.dmsAccount || []
              };
              if (!updatedDMSAccounts.some(item => item.entityId === updatedDMSAccountWithType.entityId)) {
                updatedDMSAccounts.push(updatedDMSAccountWithType);
              }
            }
          });
          updatedLocation[acctTypeName] = updatedDMSAccounts;
          return updatedLocation;
        }, {});
        dispatch(updateDMSAccountsForAllLocations(updatedAllLocations));
      }, [updateSelectedDMSAccounts, updatedSelectedLocations]);

      useEffect(() => {
        if (DMSConfigInfo && DMSConfigInfo.length) {
          const filteredCardsByApiIds = Object.keys(updatedSelectedAllLocations)
            .filter(key => DMSConfigInfo.includes(key))
            .reduce((obj, key) => {
              obj[key] = updatedSelectedAllLocations[key];
              return obj;
            }, {});
          dispatch(showAllDMSAccountsLocations(filteredCardsByApiIds));
        } else {
          dispatch(showAllDMSAccountsLocations(updatedSelectedAllLocations));
        }
      }, [updateDMSAccountsForAllLocations, updatedSelectedAllLocations, DMSConfigInfo]);

      useEffect(() => {
        const flattenedLocations = Object.entries(showAllLocations).reduce((newObj, [key, value]) => {
          const v = value.map(item => {
            return Object.assign({}, item, {
              dmsAccount: item.dmsAccount[0],
              departments: [item.departments[0]]
            });
          });
          newObj = [...newObj, ...v];
          return newObj;
        }, []);
        const filteredLocationsWithDMSAccounts = flattenedLocations && flattenedLocations.filter(location => location.dmsAccount);
        dispatch(updateDMSAccountsOnSelection(filteredLocationsWithDMSAccounts));
      }, [showAllDMSAccountsLocations, showAllLocations]);

    addNavListener(dispatch);

    return(
        <Stepper bodyConfig={bodyConfig} {...props}/>
    );
}

export default StepperContainer;
